<script setup>
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const emit = defineEmits(['close']);

const PANEL_LIMIT = 12;

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const accountId = useMapGetter('getCurrentAccountId');
const notifications = useMapGetter('notifications/getNotifications');
const notificationMetadata = useMapGetter('notifications/getMeta');
const uiFlags = useMapGetter('notifications/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetching);
const isUpdating = computed(() => uiFlags.value.isUpdating);
const unreadCount = computed(() => notificationMetadata.value.unreadCount || 0);
const hasUnread = computed(() => unreadCount.value > 0);

const visibleNotifications = computed(() =>
  notifications.value.slice(0, PANEL_LIMIT)
);

const showEmptyState = computed(
  () => !isLoading.value && visibleNotifications.value.length === 0
);

onMounted(() => {
  // Siempre refrescar al abrir: el panel es la vista de NO leídas y los
  // records del store pueden quedar desincronizados si la página marcó
  // leído por API directa (no toca records). `get` hace CLEAR + load fresco.
  store.dispatch('notifications/get', { page: 1 });
});

const onClickNotification = notification => {
  const {
    primary_actor_id: primaryActorId,
    primary_actor_type: primaryActorType,
    notification_type: notificationType,
  } = notification;
  // primary_actor puede ser null (actor borrado) -> optional chaining, no
  // destructuring con default {} (que solo cubre undefined).
  const conversationId = notification.primary_actor?.id;

  useTrack(ACCOUNT_EVENTS.OPEN_CONVERSATION_VIA_NOTIFICATION, {
    notificationType,
  });

  if (!notification.read_at) {
    store.dispatch('notifications/read', {
      id: notification.id,
      primaryActorId,
      primaryActorType,
      unreadCount: unreadCount.value,
    });
  }

  emit('close');

  if (conversationId) {
    router.push(
      `/app/accounts/${accountId.value}/conversations/${conversationId}`
    );
  }
};

const onMarkAllReadClick = () => {
  if (!hasUnread.value) return;
  useTrack(ACCOUNT_EVENTS.MARK_AS_READ_NOTIFICATIONS);
  store.dispatch('notifications/readAll');
};

const onViewAllClick = () => {
  emit('close');
  router.push({
    name: 'notifications_index',
    params: { accountId: accountId.value },
  });
};
</script>

<template>
  <div class="flex flex-col w-full sm:w-[22.5rem] text-sm">
    <header
      class="sticky top-0 z-10 flex items-center justify-between flex-shrink-0 gap-2 px-3 py-2.5 border-b border-n-weak bg-n-alpha-3 backdrop-blur-[100px]"
    >
      <div class="flex items-center gap-1.5 min-w-0">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('NOTIFICATION_PANEL.HEADER') }}
        </h3>
        <span
          v-if="hasUnread"
          class="px-1.5 py-px text-xs font-medium leading-none rounded-full text-n-ruby-11 bg-n-ruby-3"
        >
          {{ unreadCount }}
        </span>
      </div>
      <Button
        variant="link"
        color="slate"
        size="sm"
        :label="t('NOTIFICATION_PANEL.MARK_ALL_READ')"
        :disabled="!hasUnread || isUpdating"
        :is-loading="isUpdating"
        @click="onMarkAllReadClick"
      />
    </header>

    <div
      v-if="isLoading && visibleNotifications.length === 0"
      class="flex items-center justify-center gap-2 px-3 py-10 text-n-slate-11"
    >
      <Spinner />
      <span>{{ t('NOTIFICATION_PANEL.LOADING') }}</span>
    </div>

    <div
      v-else-if="showEmptyState"
      class="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center"
    >
      <span class="i-lucide-bell-off size-6 text-n-slate-10" />
      <p class="m-0 text-sm text-n-slate-11">
        {{ t('NOTIFICATION_PANEL.EMPTY_STATE') }}
      </p>
    </div>

    <ul v-else class="flex flex-col m-0 list-none">
      <li
        v-for="notification in visibleNotifications"
        :key="notification.id"
        class="flex items-start gap-2.5 px-3 py-2.5 cursor-pointer border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1 transition-colors duration-150"
        :class="{ 'bg-n-alpha-1': !notification.read_at }"
        @click="onClickNotification(notification)"
      >
        <span
          class="flex items-center justify-center flex-shrink-0 mt-1 size-2"
        >
          <span
            v-if="!notification.read_at"
            class="rounded-full size-2 bg-n-ruby-9"
          />
        </span>
        <Avatar
          v-if="notification.primary_actor?.meta?.assignee"
          :src="notification.primary_actor.meta.assignee.thumbnail"
          :name="notification.primary_actor.meta.assignee.name"
          :size="28"
          rounded-full
          class="flex-shrink-0"
        />
        <div class="flex flex-col min-w-0 gap-0.5 grow">
          <div class="flex items-center justify-between gap-2">
            <span
              class="text-sm truncate text-n-slate-12"
              :class="{ 'font-medium': !notification.read_at }"
            >
              {{ notification.push_message_title }}
            </span>
            <span class="flex-shrink-0 text-xs text-n-slate-10">
              {{ dynamicTime(notification.last_activity_at) }}
            </span>
          </div>
          <span
            v-if="notification.push_message_body"
            class="text-xs truncate text-n-slate-11"
          >
            {{ notification.push_message_body }}
          </span>
        </div>
      </li>
    </ul>

    <footer
      class="sticky bottom-0 z-10 flex-shrink-0 px-2 py-1.5 border-t border-n-weak bg-n-alpha-3 backdrop-blur-[100px]"
    >
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        class="w-full"
        justify="center"
        :label="t('NOTIFICATION_PANEL.VIEW_ALL')"
        @click="onViewAllClick"
      />
    </footer>
  </div>
</template>
